import type { Bilingual } from '@/shared/types/api';

/**
 * The buyer's sign-in step 2 — choosing an organisation (auth contract §1).
 *
 * An address and a password that open two or more active organisations do not
 * open a session: sign-in answers `{ step: 'organisation_selection',
 * selection }` instead, and the person chooses. The selection is a short-lived
 * server-side challenge; the client holds only its opaque token, in the
 * sign-in screen's own state — never in the session cache, the URL or storage —
 * so a reload or a 401 starts again at step 1.
 */
export interface SelectableOrganisation {
  /** The organisation's public id: opaque, never a slug. */
  id: string;
  name: Bilingual;
  display_name: string;
}

export interface OrganisationSelection {
  token: string;
  expires_at: string;
  organisations: SelectableOrganisation[];
}

export interface OrganisationChoicePayload {
  step: 'organisation_selection';
  selection: OrganisationSelection;
}

/**
 * Whether a sign-in answer is a STEP rather than a session.
 *
 * The contract's one rule for the client: `data.step` present means a further
 * step; otherwise the body is the session. Any step — this one, or one a later
 * server adds — is kept out of the session cache.
 */
export function isSignInStep(payload: unknown): boolean {
  if (payload === null || typeof payload !== 'object') return false;

  const step = (payload as { step?: unknown }).step;

  return step !== undefined && step !== null;
}

function isOrganisation(value: unknown): value is SelectableOrganisation {
  if (value === null || typeof value !== 'object') return false;

  const { id, name } = value as { id?: unknown; name?: unknown };

  return typeof id === 'string' && id !== '' && name !== null && typeof name === 'object';
}

/** A step-2 answer the client can act on: a token and at least one organisation. */
export function isOrganisationChoice(payload: unknown): payload is OrganisationChoicePayload {
  if (!isSignInStep(payload)) return false;

  const { step, selection } = payload as { step: unknown; selection?: unknown };

  if (step !== 'organisation_selection' || selection === null || typeof selection !== 'object') {
    return false;
  }

  const { token, organisations } = selection as { token?: unknown; organisations?: unknown };

  return (
    typeof token === 'string' &&
    token !== '' &&
    Array.isArray(organisations) &&
    organisations.length > 0 &&
    organisations.every(isOrganisation)
  );
}

/** The name to show: the reader's language, else the server's display name. */
export function organisationName(
  organisation: SelectableOrganisation,
  locale: keyof Bilingual,
): { text: string; lang: keyof Bilingual | undefined } {
  const localised = organisation.name[locale];

  if (typeof localised === 'string' && localised.trim() !== '') {
    return { text: localised, lang: locale };
  }

  return { text: organisation.display_name, lang: undefined };
}
